/**
 * chatSseClient.ts - Low-level SSE helper for AI chat streaming
 *
 * Wraps the `/ua/chat/sse/complete` endpoint with a typed helper that
 * creates an EventSource and wires up all documented events.
 */

import { isLocalhost } from "core";

// Base URL for SSE endpoint – mirrors API root configuration in App.tsx
const API_BASE =
  typeof window !== "undefined" && isLocalhost()
    ? "http://localhost:3002"
    : "";

const SSE_ENDPOINT = `${API_BASE}/ua/chat/sse/complete`;

export type ChatSSEStage =
  | "authentication"
  | "configuration"
  | "creating_chat"
  | "saving_user_message"
  | "vector_search"
  | "generating"
  | "saving_ai_response"
  | "enhance"
  | string;

export interface StatusEventPayload {
  message: string;
  stage?: ChatSSEStage;
}

export interface RootPostCreatedPayload {
  postId: string;
  post: any; // SerializedPost from backend
}

export interface UserMessageSavedPayload {
  postId: string;
  post: any; // SerializedPost
}

export interface VectorSearchCompletePayload {
  post: any; // SerializedPost
  vectorSearchResults?: any[];
}

export interface ChunkEventPayload {
  chunk: string;
  assistantPostId: string;
}

export interface CompleteEventPayload {
  fullText: string;
  nsHtml?: string; // HTML-rendered content (preferred for display)
  assistantPostId: string;
  tokenUsage?: any;
}

export interface DoneEventPayload {
  message: string;
  rootPostId?: string;
  userPostId?: string;
  assistantPostId: string;
}

export interface ErrorEventPayload {
  error: string;
}

export interface ChatSSEParams {
  postId: string; // 'new' for new chat or existing root post id
  message: string;
  circleId?: string; // required when postId === 'new'
  token?: string; // optional – backend may also use cookies
  assistant?: string; // assistant id
  prompt?: string; // prompt id
  filters?: {
    dateRange?: {
      customFrom?: string | null;
      customTo?: string | null;
    };
    authors?: string[];
    persons?: string[];
    organizations?: string[];
    locations?: string[];
    categoryCodes?: string[];
  };
}

export interface ChatSSEHandlers {
  onStatus?: (payload: StatusEventPayload) => void;
  onRootPostCreated?: (payload: RootPostCreatedPayload) => void;
  onUserMessageSaved?: (payload: UserMessageSavedPayload) => void;
  onVectorSearchComplete?: (payload: VectorSearchCompletePayload) => void;
  onChunk?: (payload: ChunkEventPayload) => void;
  onComplete?: (payload: CompleteEventPayload) => void;
  onDone?: (payload: DoneEventPayload) => void;
  onError?: (payload: ErrorEventPayload) => void;
  // Low-level connection error (network, CORS, etc.)
  onConnectionError?: (event: Event) => void;
}

export interface ChatSSEConnection {
  eventSource: EventSource;
  close: () => void;
}

/**
 * Open an SSE connection to the chat completion endpoint.
 *
 * The caller is responsible for closing the connection when finished.
 */
export function openChatSSE(
  params: ChatSSEParams,
  handlers: ChatSSEHandlers = {}
): ChatSSEConnection {
  const searchParams = new URLSearchParams();
  searchParams.set("postId", params.postId);
  searchParams.set("message", params.message);

  if (params.circleId && params.postId === "new") {
    searchParams.set("circleId", params.circleId);
  }

  if (params.token) {
    searchParams.set("token", params.token);
  }

  if (params.assistant) {
    searchParams.set("assistant", params.assistant);
  }

  if (params.prompt) {
    searchParams.set("prompt", params.prompt);
  }

  // Add filters if provided
  if (params.filters) {
    const filterPayload: any = {};
    
    // Add date range if present
    if (params.filters.dateRange?.customFrom || params.filters.dateRange?.customTo) {
      filterPayload.dateRange = {
        from: params.filters.dateRange.customFrom,
        to: params.filters.dateRange.customTo,
      };
    }
    
    // Add array filters if they have values
    if (params.filters.authors && params.filters.authors.length > 0) {
      filterPayload.authors = params.filters.authors;
    }
    if (params.filters.persons && params.filters.persons.length > 0) {
      filterPayload.persons = params.filters.persons;
    }
    if (params.filters.organizations && params.filters.organizations.length > 0) {
      filterPayload.organizations = params.filters.organizations;
    }
    if (params.filters.locations && params.filters.locations.length > 0) {
      filterPayload.locations = params.filters.locations;
    }
    if (params.filters.categoryCodes && params.filters.categoryCodes.length > 0) {
      filterPayload.categoryCodes = params.filters.categoryCodes;
    }
    
    // Only add filters param if we have any filters
    if (Object.keys(filterPayload).length > 0) {
      searchParams.set("filters", JSON.stringify(filterPayload));
    }
  }

  // searchParams.set("searchType", "hybrid");
  // searchParams.set("vectorWeight", "0.9");
  // searchParams.set("textWeight", "0.1");
  // searchParams.set("searchK", "20");


  const url = `${SSE_ENDPOINT}?${searchParams.toString()}`;

  const eventSource = new EventSource(url);

  // Helper to safely parse JSON event data
  const parse = <T>(event: MessageEvent): T | null => {
    try {
      return JSON.parse(event.data) as T;
    } catch (error) {
      console.error("Failed to parse SSE event data", error, event.data);
      return null;
    }
  };

  // Wire up documented events
  eventSource.addEventListener("status", (event: MessageEvent) => {
    if (!handlers.onStatus) return;
    const data = parse<StatusEventPayload>(event);
    if (data) handlers.onStatus(data);
  });

  eventSource.addEventListener("root_post_created", (event: MessageEvent) => {
    if (!handlers.onRootPostCreated) return;
    const data = parse<RootPostCreatedPayload>(event);
    if (data) handlers.onRootPostCreated(data);
  });

  eventSource.addEventListener("user_message_saved", (event: MessageEvent) => {
    if (!handlers.onUserMessageSaved) return;
    const data = parse<UserMessageSavedPayload>(event);
    if (data) handlers.onUserMessageSaved(data);
  });

  eventSource.addEventListener(
    "vector_search_complete",
    (event: MessageEvent) => {
      if (!handlers.onVectorSearchComplete) return;
      const data = parse<VectorSearchCompletePayload>(event);
      if (data) handlers.onVectorSearchComplete(data);
    }
  );

  eventSource.addEventListener("chunk", (event: MessageEvent) => {
    if (!handlers.onChunk) return;
    const data = parse<ChunkEventPayload>(event);
    if (data) handlers.onChunk(data);
  });

  eventSource.addEventListener("complete", (event: MessageEvent) => {
    if (!handlers.onComplete) return;
    const data = parse<CompleteEventPayload>(event);
    if (data) handlers.onComplete(data);
  });

  eventSource.addEventListener("done", (event: MessageEvent) => {
    if (!handlers.onDone) return;
    const data = parse<DoneEventPayload>(event);
    if (data) handlers.onDone(data);
  });

  eventSource.addEventListener("error", (event: MessageEvent) => {
    if (!handlers.onError) return;
    const data = parse<ErrorEventPayload>(event);
    if (data) handlers.onError(data);
  });

  eventSource.onerror = (event) => {
    console.error("Chat SSE connection error", event);
    if (handlers.onConnectionError) {
      handlers.onConnectionError(event);
    }
  };

  return {
    eventSource,
    close: () => eventSource.close(),
  };
}


